import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { DealOptionField } from './types';

function formatDealOptionFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function textField(
  label: string,
  value: unknown,
  group: string,
  extras: Partial<DealOptionField> = {},
): DealOptionField {
  const { layout, ...rest } = extras;
  return {
    label,
    value: formatDealOptionFieldValue(value),
    ...rest,
    layout: { columns: 3, group, ...layout },
  };
}

function numberField(label: string, value: unknown, group: string): DealOptionField {
  return textField(label, value, group, { type: 'number' });
}

function dateField(label: string, value: unknown, group: string): DealOptionField {
  return textField(label, value, group, { type: 'date' });
}

export interface DealOptionDetailFieldGroup {
  columns: 1 | 2 | 3 | 4;
  fields: Array<DealOptionField>;
  title?: string;
}

export function groupDealOptionDetailFields(fields: Array<DealOptionField>): Array<DealOptionDetailFieldGroup> {
  return fields.reduce<Array<DealOptionDetailFieldGroup>>((groups, field) => {
    const title = field.layout?.group;
    const columns = field.layout?.columns ?? 3;
    const last = groups[groups.length - 1];
    if (last && last.title === title) {
      last.fields.push(field);
      last.columns = Math.max(last.columns, columns) as DealOptionDetailFieldGroup['columns'];
      return groups;
    }

    groups.push({ columns, title, fields: [field] });
    return groups;
  }, []);
}

export function buildDealOptionDetailFields(deal: OpportunityRelatedDeal): Array<DealOptionField> {
  return [
    textField('Deal Option Name', deal.name, 'Identity', { layout: { columns: 2, group: 'Identity' } }),
    textField('Salesforce Id', deal.sfid, 'Identity', { layout: { columns: 2, group: 'Identity' } }),
    textField('Room Asset', deal.room_asset?.name, 'Identity', { layout: { columns: 2, group: 'Identity' } }),
    textField('Currency', deal.currencyisocode, 'Commercial'),
    numberField('Number of Nights', deal.number_of_nights__c, 'Commercial'),
    numberField('Allocation', deal.allocation__c, 'Commercial'),
    numberField('Max Extra Nights', deal.max_extra_nights__c, 'Commercial'),
    numberField('Cost Price', deal.cost_price__c, 'Commercial'),
    numberField('Converted Cost', deal.converted_cost__c, 'Commercial'),
    numberField('Cost Price Currency Price', deal.cost_price_currency_price__c, 'Commercial'),
    numberField('Sell Price', deal.price__c, 'Commercial'),
    numberField('Margin %', deal.margin_percent__c, 'Commercial'),
    textField('Margin Check', deal.margin_check__c, 'Commercial'),
    numberField('Extra Night Margin', deal.extra_night_margin__c, 'Commercial'),
    numberField('Offer Discount', deal.offer_discount__c, 'Commercial'),
    numberField('Inclusions Total Value', deal.inclusions_total_value__c, 'Commercial'),
    numberField('Suggested Value', deal.suggested_value__c, 'Commercial'),
    textField('Channel Fare Type', deal.channel_fare_type__c, 'Travel', {
      layout: { columns: 2, group: 'Travel' },
    }),
    textField('Child Age Permitted', deal.child_age_permitted__c, 'Travel', {
      layout: { columns: 2, group: 'Travel' },
    }),
    dateField('Travel Start Date', deal.travel_start_date__c, 'Travel'),
    dateField('Travel End Date', deal.travel_end_date__c, 'Travel'),
    numberField('Included Adults', deal.included_number_of_adults__c, 'Occupancy'),
    numberField('Included Children', deal.included_number_of_children__c, 'Occupancy'),
    numberField('Included Teenagers', deal.included_number_of_teenagers__c, 'Occupancy'),
    numberField('Included Infants', deal.included_number_of_infant__c, 'Occupancy'),
    numberField('Max Room Pax', deal.max_room_pax__c, 'Occupancy'),
    numberField('Room Size', deal.room_size__c, 'Occupancy'),
    textField('Bundled Experiences', deal.bundled_experiences__c, 'Bundled Experiences', {
      layout: { columns: 1, group: 'Bundled Experiences', span: 'full' },
      multiline: true,
    }),
    numberField('Number of Bundled Experiences', deal.number_of_bundled_experience__c, 'Bundled Experiences'),
  ];
}

export const DEAL_OPTION_FIELDS: Array<DealOptionField> = [];
